import type { SyncDelta, SyncServerMessage } from '@couplespace/sync';

import { getNativeSessionToken } from '../auth/capacitor-session';
import { getApiBaseUrl } from '../env';
import { setServerReachable } from '../net/net-status';

import { nextReconnectDelay } from './backoff';
import { routeDeltas, type DeltaRoute } from './route-delta';

export type LiveStatus = 'idle' | 'connecting' | 'open' | 'closed';

type DeltaListener = (route: DeltaRoute, deltas: SyncDelta[]) => void;
type Listener = () => void;

async function liveSocketUrl(): Promise<string> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const wsBase = base.replace(/^http/, 'ws');
  const token = await getNativeSessionToken();
  if (token) {
    return `${wsBase}/ws/sync?token=${encodeURIComponent(token)}`;
  }
  return `${wsBase}/ws/sync`;
}

/**
 * One WebSocket per tab that receives live change hints. There is no client
 * cursor: after any reconnect, views refetch what they show.
 */
class LiveConnection {
  private ws: WebSocket | null = null;
  private status: LiveStatus = 'idle';
  private started = false;
  private attempt = 0;
  private everOpened = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private deltaListeners = new Set<DeltaListener>();
  private reconnectListeners = new Set<Listener>();
  private statusListeners = new Set<Listener>();

  start(): void {
    if (this.started || typeof WebSocket === 'undefined') {
      return;
    }
    this.started = true;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.reconnectNow);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
    void this.connect();
  }

  stop(): void {
    this.started = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.reconnectNow);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.clearReconnectTimer();
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    this.setStatus('idle');
  }

  getStatus(): LiveStatus {
    return this.status;
  }

  subscribeDeltas(listener: DeltaListener): () => void {
    this.deltaListeners.add(listener);
    return () => this.deltaListeners.delete(listener);
  }

  subscribeReconnect(listener: Listener): () => void {
    this.reconnectListeners.add(listener);
    return () => this.reconnectListeners.delete(listener);
  }

  subscribeStatus(listener: Listener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private setStatus(status: LiveStatus): void {
    if (this.status === status) {
      return;
    }
    this.status = status;
    for (const listener of this.statusListeners) {
      listener();
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private readonly reconnectNow = (): void => {
    if (!this.started || this.status === 'open' || this.status === 'connecting') {
      return;
    }
    this.clearReconnectTimer();
    this.attempt = 0;
    void this.connect();
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.reconnectNow();
    }
  };

  private scheduleReconnect(): void {
    if (!this.started || this.reconnectTimer) {
      return;
    }
    const delay = nextReconnectDelay(this.attempt);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  private async connect(): Promise<void> {
    if (!this.started) {
      return;
    }
    this.setStatus('connecting');
    let url: string;
    try {
      url = await liveSocketUrl();
    } catch {
      this.setStatus('closed');
      this.scheduleReconnect();
      return;
    }
    if (!this.started) {
      return;
    }

    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) {
        return;
      }
      ws.send(JSON.stringify({ type: 'subscribe', since: null }));
      this.attempt = 0;
      setServerReachable(true);
      this.setStatus('open');
      if (this.everOpened) {
        for (const listener of this.reconnectListeners) {
          listener();
        }
      }
      this.everOpened = true;
    };

    ws.onmessage = (event) => {
      if (this.ws === ws) {
        this.handleMessage(ws, String(event.data));
      }
    };

    ws.onclose = () => {
      if (this.ws !== ws) {
        return;
      }
      this.ws = null;
      this.setStatus('closed');
      this.scheduleReconnect();
    };
  }

  private handleMessage(ws: WebSocket, raw: string): void {
    let message: SyncServerMessage;
    try {
      message = JSON.parse(raw) as SyncServerMessage;
    } catch {
      return;
    }
    if (message.type === 'ping') {
      ws.send(JSON.stringify({ type: 'pong' }));
      return;
    }
    if (message.type !== 'delta' || message.changes.length === 0) {
      return;
    }
    const route = routeDeltas(message.changes);
    for (const listener of this.deltaListeners) {
      try {
        listener(route, message.changes);
      } catch (err) {
        console.error('[CS-LIVE] delta listener failed', err);
      }
    }
  }
}

const connection = new LiveConnection();

export function startLiveConnection(): void {
  connection.start();
}

export function stopLiveConnection(): void {
  connection.stop();
}

export function getLiveStatus(): LiveStatus {
  return connection.getStatus();
}

export function subscribeLiveDeltas(listener: DeltaListener): () => void {
  return connection.subscribeDeltas(listener);
}

/** Fires when the socket reopens after a drop, so views can refetch what they missed. */
export function subscribeLiveReconnect(listener: Listener): () => void {
  return connection.subscribeReconnect(listener);
}

export function subscribeLiveStatus(listener: Listener): () => void {
  return connection.subscribeStatus(listener);
}
